import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import { catalogApiRef, useEntity } from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';
import useAsync from 'react-use/lib/useAsync';
import { getEntityRelations } from '@backstage/plugin-catalog-react';
import {
  toMedidataComponent,
  MedidataComponent,
} from '@internal/plugin-medidata-common';

const uniq = (entities: Entity[]): Entity[] =>
  entities.filter((entity, index) => entities.indexOf(entity) === index);

export const useEntities = (
  kinds = ['Component', 'API', 'System', 'Domain'],
  fields = [
    'spec',
    'kind',
    'metadata.name',
    'metadata.title',
    'metadata.description',
    'relations',
  ],
): Entity[] => {
  const catalogApi = useApi(catalogApiRef);

  const { value: entities } = useAsync(async () => {
    const entitiesList = await catalogApi.getEntities({
      filter: { kind: kinds },
      fields: fields,
    });
    return entitiesList.items;
  }, [catalogApi]);

  return entities || [];
};

// Uses a list of entities to resolve the entities at the other side of a relationship
export const getRelatedEntities = (
  entities: Entity[],
  relationship: string,
  list: Entity[],
) => {
  const refs = entities.flatMap(entity =>
    getEntityRelations(entity, relationship).map(stringifyEntityRef),
  );
  const relatedEntities = list.filter(component =>
    refs.includes(stringifyEntityRef(component)),
  );
  return uniq(relatedEntities);
};

export type LinkData = {
  title: string;
  url: string;
};

const FOSSA_ANNOTATION = 'fossa.io/project-name';
export const isFossaAvailable = (entity: Entity) =>
  Boolean(entity.metadata.annotations?.[FOSSA_ANNOTATION]);

export const useMedidataComponent = (): MedidataComponent => {
  const { entity } = useEntity();
  return toMedidataComponent(entity);
};

export const isOrganization = (x: Entity): boolean =>
  x.spec?.type === 'solution-line' || x.spec?.type === 'organization';

export const isSolutionLineGroup = (x: Entity): boolean =>
  x.spec?.type === 'solution-line';
